import { PLATFORM_BRAND } from "../config/platformBrand";

export type BrandThemeInput = {
  primaryColor?: string | null;
  secondaryColor?: string | null;
};

const normalizeHex = (value: string | null | undefined, fallback: string): string => {
  if (!value) return fallback;
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    const [, a, b, c] = trimmed;
    return `#${a}${a}${b}${b}${c}${c}`;
  }
  return fallback;
};

export const hexToRgb = (
  hex: string,
): { r: number; g: number; b: number } | null => {
  const normalized = normalizeHex(hex, "");
  if (!normalized) return null;
  const n = Number.parseInt(normalized.slice(1), 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
};

const setVar = (name: string, value: string): void => {
  document.documentElement.style.setProperty(name, value);
};

/**
 * Aplica la paleta del comercio (o la de la plataforma) a las CSS variables.
 * Así Home / Join / Status cambian de atmósfera sin hardcodear colores.
 */
export const applyBrandTheme = (input: BrandThemeInput = {}): void => {
  const primary = normalizeHex(input.primaryColor, PLATFORM_BRAND.primaryColor);
  const secondary = normalizeHex(
    input.secondaryColor,
    PLATFORM_BRAND.accentColor,
  );

  const primaryRgb = hexToRgb(primary);
  const secondaryRgb = hexToRgb(secondary);

  setVar("--primary-color", primary);
  setVar("--secondary-color", secondary);
  setVar("--color-accent", primary);
  setVar("--color-accent-hover", primary);
  setVar("--brand-primary", primary);
  setVar("--terra", primary);
  setVar("--brand-secondary", secondary);
  setVar("--brand-accent", secondary);
  setVar("--sage", secondary);
  setVar("--brand-night", PLATFORM_BRAND.nightColor);

  if (primaryRgb) {
    setVar("--brand-primary-rgb", `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    setVar("--brand-night-rgb", `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  }
  if (secondaryRgb) {
    setVar(
      "--brand-secondary-rgb",
      `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`,
    );
  }
};

/** Restablece la identidad visual del SaaS (pantallas sin comercio). */
export const applyPlatformTheme = (): void => {
  applyBrandTheme({
    primaryColor: PLATFORM_BRAND.primaryColor,
    secondaryColor: PLATFORM_BRAND.accentColor,
  });
  setVar("--brand-night", PLATFORM_BRAND.nightColor);
  setVar("--brand-night-rgb", "59, 42, 32");
  setVar("--brand-primary", PLATFORM_BRAND.primaryColor);
  setVar("--terra", PLATFORM_BRAND.primaryColor);
  setVar("--brand-accent", PLATFORM_BRAND.accentColor);
  setVar("--sage", PLATFORM_BRAND.accentColor);
  setVar("--brand-highlight", PLATFORM_BRAND.highlightColor);
  setVar("--butter", PLATFORM_BRAND.highlightColor);
  setVar("--color-background", PLATFORM_BRAND.backgroundColor);
  setVar("--cream", PLATFORM_BRAND.backgroundColor);
  setVar("--color-surface", PLATFORM_BRAND.surfaceColor);
  setVar("--color-primary", PLATFORM_BRAND.textPrimary);
  setVar("--color-text", PLATFORM_BRAND.textPrimary);
  setVar("--cocoa", PLATFORM_BRAND.textPrimary);
  setVar("--color-text-muted", PLATFORM_BRAND.textSecondary);
};
